import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { ServerStatusBar } from "@/components/ServerStatus";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { Keyboard, Loader2, MapPin, X } from "lucide-react";

// PLACE IN SECTOR, ON THE CARD HE ALREADY LANDS ON (his instruction, 2026-10-08: *"Forget a
// separate truck screen — that's just more noise. Put PLACE IN SECTOR on the quick view that
// opens when I scan a truck tag."*) Built to the layout he approved, step for step.
//
// WHAT IT REPLACES. The card's MOVE action opened a scan overlay — *"Scan destination sector"* —
// so moving a truck meant walking to the destination's label and scanning it, or typing a code
// from memory while standing in a yard with gloves on. The sectors are now BUTTONS, his most
// recently used first, with the truck count on each so he can see where the fleet actually sits.
//
// THE RULES IT KEEPS
//   · Nothing is typed unless he chooses to — scanning and typing stay, LAST, never first,
//     because a brand-new sector has to be reachable the first time.
//   · The sector it is already in is shown and NOT pressable, so nobody taps it and wonders why
//     nothing happened.
//   · The sentence he confirms is the SERVER'S sentence (confirm=false preview), so the screen
//     can never show one thing and the record say another.
//   · An existing location is always shown as a FROM. Nothing is overwritten silently.
export function FloorPlaceSector({ donorId, stock, current, onClose, onPlaced }) {
  const [sectors, setSectors] = useState(null);
  const [pick, setPick] = useState(null);      // {code, name, label}
  const [prompt, setPrompt] = useState("");    // the server's own confirmation sentence
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/donors/place-sectors")
      .then(({ data }) => setSectors(data.items || []))
      .catch((e) => { setSectors([]); setErr(apiErrorMessage(e) || "Sector list unavailable."); });
  }, []);

  // STEP 2 → STEP 3. The preview call is what produces the words on the confirm screen.
  const choose = async (sector) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/donors/${donorId}/place`,
        { location_scan: sector.code, confirm: false });
      if (!data.needs_confirm) { setErr(data.message || "That sector could not be used."); return; }
      setPick({ ...sector, ...(data.destination || {}) });
      setPrompt(data.prompt || "");
      setScan(false);
    } catch (e) { setErr(apiErrorMessage(e) || "Could not check that sector."); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/donors/${donorId}/place`,
        { location_scan: pick.code, confirm: true });
      if (!data.ok) { setErr(data.message || "Move failed."); return; }
      onPlaced(data);
    } catch (e) { setErr(apiErrorMessage(e) || "Move failed."); }
    finally { setBusy(false); }
  };

  // ---- STEP 3 — CONFIRM, IN HIS WORDS, WITH THE FROM AND THE TO ----------------------------
  if (pick) {
    return (
      <div data-testid="floor-place-confirm" className="fixed inset-x-0 top-vv h-vv z-[86] bg-white flex flex-col overflow-y-auto">
        <ServerStatusBar />
        <div className="flex-1 flex flex-col justify-center gap-7 p-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-1">
              {current?.label ? "Move" : "Place"}
            </div>
            <div data-testid="floor-place-confirm-stock" className="font-display text-4xl font-black tracking-tight">
              {stock || "this truck"}
            </div>
          </div>
          <div className="space-y-3">
            {/* NO "FROM" LINE IS INVENTED for a truck that has never been placed. */}
            {current?.label && (
              <div className="flex items-baseline gap-3">
                <span className="w-16 shrink-0 text-right text-base font-mono uppercase tracking-wider text-slate-400">from</span>
                <span data-testid="floor-place-from" className="text-[28px] leading-tight font-bold text-slate-500">{current.label}</span>
              </div>
            )}
            <div className="flex items-baseline gap-3">
              <span className="w-16 shrink-0 text-right text-base font-mono uppercase tracking-wider text-slate-400">to</span>
              <span data-testid="floor-place-to" className="text-[34px] leading-tight font-black text-slate-900">{pick.name || pick.label || pick.code}</span>
            </div>
          </div>
          {prompt && (
            <p data-testid="floor-place-prompt" className="text-base text-slate-500 leading-relaxed">{prompt}</p>
          )}
          {err && <p data-testid="floor-place-error" className="text-base font-bold text-[#B01E28]">{err}</p>}
          <div className="space-y-3">
            <button type="button" data-testid="floor-place-yes" onClick={commit} disabled={busy}
              className="w-full h-24 rounded-xl bg-[#B01E28] text-white text-2xl font-black flex items-center justify-center gap-3 active:scale-95 active:bg-[#8B1820] transition-[transform,background-color] duration-100 disabled:opacity-60">
              {busy ? <Loader2 className="w-8 h-8 animate-spin" /> : <MapPin className="w-8 h-8" strokeWidth={2.2} />}
              YES — {current?.label ? "MOVE IT" : "PLACE IT"}
            </button>
            <button type="button" data-testid="floor-place-no" onClick={() => { setPick(null); setPrompt(""); }}
              disabled={busy}
              className="w-full h-[72px] rounded-xl border-2 border-[#E2E8F0] bg-white text-xl font-bold text-slate-700 active:scale-95 active:bg-slate-100 transition-[transform,background-color] duration-100">
              NO
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- STEP 2 — THE SECTOR BUTTONS. NO TYPING, NO SMALL CONTROLS -------------------------
  return (
    <div data-testid="floor-place-sectors" className="fixed inset-x-0 top-vv h-vv z-[86] bg-white flex flex-col overflow-y-auto">
      <ServerStatusBar />
      <div className="h-20 flex items-center justify-between px-6 border-b border-[#E2E8F0] shrink-0">
        <div className="min-w-0">
          <div className="text-2xl font-black tracking-tight truncate">PLACE {stock || "TRUCK"}</div>
          <div data-testid="floor-place-now" className="text-base font-medium truncate">
            {current?.label
              ? <span className="text-slate-500">Now in: {current.label}</span>
              : <span className="text-[#C76714] font-bold">NOT PLACED</span>}
          </div>
        </div>
        <button type="button" data-testid="floor-place-close" onClick={onClose}
          className="w-16 h-16 rounded-xl border-2 border-[#E2E8F0] flex items-center justify-center active:scale-95 active:bg-slate-100 transition-[transform,background-color] duration-100 shrink-0 ml-4">
          <X className="w-8 h-8" strokeWidth={2.2} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
        {err && <p data-testid="floor-place-error" className="text-base font-bold text-[#B01E28]">{err}</p>}
        {sectors === null && (
          <div className="py-16 flex justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>
        )}
        {sectors?.length === 0 && (
          <p data-testid="floor-place-no-sectors" className="text-lg text-slate-500 leading-relaxed">
            No yard sectors are set up yet. Scan or type a code below — any location works.
          </p>
        )}
        {(sectors || []).map((s) => {
          const here = current?.id && String(current.id) === String(s.id);
          return (
            <button key={s.id} type="button" data-testid={`floor-place-sector-${s.code}`}
              onClick={() => !here && choose(s)} disabled={busy || here}
              className={`w-full h-[88px] rounded-xl border-2 px-5 flex items-center justify-between gap-3 text-left transition-[transform,background-color] duration-100 ${
                here ? "border-slate-200 bg-slate-100 text-slate-400"
                     : "border-[#E2E8F0] bg-white text-slate-900 active:scale-95 active:bg-slate-100"}`}>
              <span className="min-w-0">
                <span className="block text-[22px] font-black leading-tight truncate">{s.name || s.code}</span>
                {s.holding && !here && <span className="block text-sm font-bold text-[#C76714]">holding area — parked, not put away</span>}
              </span>
              <span className={`shrink-0 text-base font-mono ${here ? "font-bold text-slate-500" : "text-slate-400"}`}>
                {here ? "← IT IS ALREADY HERE" : `${s.trucks} here`}
              </span>
            </button>
          );
        })}

        {/* SCANNING AND TYPING ARE KEPT, AT THE BOTTOM — a brand-new sector has to be reachable
            the first time. It is never the first thing offered. */}
        <button type="button" data-testid="floor-place-somewhere-else" onClick={() => setScan(true)}
          className="w-full h-16 rounded-xl border-2 border-dashed border-slate-300 bg-white flex items-center justify-center gap-3 text-lg font-bold text-slate-600 active:scale-95 active:bg-slate-100 transition-[transform,background-color] duration-100">
          <Keyboard className="w-6 h-6" strokeWidth={2.2} /> SOMEWHERE ELSE — scan or type a code
        </button>
      </div>

      <FloorScanOverlay open={scan} testid="floor-place-scan"
        title="Scan or type the sector" sub={`Place ${stock || "truck"}`} submitLabel="Use this place"
        onScan={(code) => choose({ id: null, code, name: code })} onClose={() => setScan(false)} busy={busy} />
    </div>
  );
}
